import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import {
  Sparkles, Star, Moon, Sun, Flame, Hash, Waves, Home as HomeIcon,
  ScrollText, Package, Radio, ShoppingBag, ArrowUpRight, Quote, Users, Clock
} from "lucide-react";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const HERO_IMG = "https://images.unsplash.com/photo-1729335511883-29eade10006b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w0NjAxODF8MHwxfHNlYXJjaHw0fHxhc3Ryb2xvZ3klMjBjb3Ntb3N8ZW58MHx8fHwxNzgzOTIxMzAyfDA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  { key: "astrology", name: "Astrology", tag: "Jyotish Shastra", icon: Star,
    blurb: "Vedic birth chart analysis, dashas, transits & remedies." },
  { key: "numerology", name: "Numerology", tag: "Ank Vidya", icon: Hash,
    blurb: "Birth number, destiny number & name-corrections for prosperity." },
  { key: "tarot", name: "Tarot", tag: "Card Wisdom", icon: ScrollText,
    blurb: "Live readings for love, career and inner guidance." },
  { key: "vastu", name: "Vastu", tag: "Sacred Space", icon: HomeIcon,
    blurb: "Balance your home & office with ancient directional science." },
  { key: "yantra", name: "Yantra · Mantra · Tantra", tag: "Energy Rituals", icon: Flame,
    blurb: "Empowered yantras, personalised mantras & tantric remedies." },
  { key: "pandit", name: "Pandit Ji Booking", tag: "Poojas · Yagyas · Path", icon: Sparkles,
    blurb: "Book learned pandits for havan, griha-pravesh & shraadh." },
];

const MARQUEE_ITEMS = [
  "ASTROLOGY", "•", "NUMEROLOGY", "•", "TAROT", "•", "VASTU", "•",
  "YANTRA · MANTRA · TANTRA", "•", "POOJA & YAGYA", "•", "AUTO AI KUNDALI",
  "•", "LIVE DARSHAN", "•", "SPIRITUAL MARKETPLACE", "•",
];

const TESTIMONIALS = [
  { name: "Anjali Verma", role: "Marketing Manager, Bengaluru",
    text: "The AI Kundali blew my mind — it captured my Dasha shifts almost eerily well. Consulted Pt. Rajesh after and things aligned." },
  { name: "Vikram Rao", role: "Founder, Mumbai",
    text: "Booked a griha-pravesh pooja on Vedacosmos in under 2 minutes. The pandit ji arrived on time with all samagri included." },
  { name: "Priya Nair", role: "Product Designer, Kochi",
    text: "The daily horoscope + Guru-ji chatbot is my morning ritual now. Feels like a real astrologer in my pocket." },
];

export default function Home() {
  const s = useSettings();
  const [zodiacs, setZodiacs] = useState([]);
  const [products, setProducts] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [z, p, e] = await Promise.all([
          api.get("/zodiacs"),
          api.get("/products"),
          api.get("/live-events"),
        ]);
        setZodiacs(z.data);
        setProducts(p.data.slice(0, 4));
        setEvents(e.data.slice(0, 3));
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  return (
    <div data-testid="home-page" className="bg-[#050507] text-[#EAE6DF]">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="cosmos" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-[#050507]" />
          <div className="absolute inset-0 starfield opacity-40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-32 md:pt-28 md:pb-40 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-[0.35em] text-[#D4AF37] mb-5 flex items-center gap-3">
              <span className="w-8 h-px bg-[#D4AF37]" /> Vedic Super App · Est. 2026
            </div>
            <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tighter text-[#EAE6DF]">
              Ancient Wisdom.<br />
              <span className="italic gold-gradient">Modern Technology.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg text-[#A0A4AB] leading-relaxed">
              Auto AI Kundali, live astrologers, pandit bookings, sacred marketplace and daily
              spiritual guidance — reimagined for the seeker of 2026.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/kundali"
                data-testid="hero-cta-kundali"
                className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition-colors duration-200"
              >
                Generate Free AI Kundali
                <ArrowUpRight size={18} className="group-hover:rotate-12 transition-transform" />
              </Link>
              <Link
                to="/astrologers"
                data-testid="hero-cta-astrologers"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/20 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              >
                Talk to an Astrologer
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { n: "12L+", l: "Kundalis Cast" },
                { n: "8,400+", l: "Verified Gurus" },
                { n: "4.9★", l: "Seeker Rating" },
              ].map((s) => (
                <div key={s.l}>
                  <div className="font-serif text-3xl gold-gradient">{s.n}</div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#A0A4AB] mt-1">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — rotating chart */}
          <div className="md:col-span-5 relative hidden md:block h-[520px]">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <div className="relative w-[440px] h-[440px]">
                <div className="absolute inset-0 rounded-full border border-[#D4AF37]/30 slow-spin" />
                <div className="absolute inset-6 rounded-full border border-[#D4AF37]/20 reverse-spin" />
                <div className="absolute inset-14 rounded-full border border-white/10 slow-spin" />
                <div className="absolute inset-24 rounded-full border border-[#D4AF37]/50" />
                {["♈","♉","♊","♋","♌","♍","♎","♏","♐","♑","♒","♓"].map((s, i) => {
                  const a = (i * 30 * Math.PI) / 180 - Math.PI / 2;
                  const r = 190;
                  const x = 220 + r * Math.cos(a);
                  const y = 220 + r * Math.sin(a);
                  return (
                    <div key={i} className="absolute font-serif text-2xl text-[#D4AF37]"
                         style={{ left: x - 10, top: y - 14 }}>
                      {s}
                    </div>
                  );
                })}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="font-deva text-4xl text-[#D4AF37] leading-none">ॐ</div>
                    <div className="text-[10px] uppercase tracking-[0.3em] mt-2 text-[#A0A4AB]">
                      Nakshatra Chakra
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Marquee ribbon */}
        <div className="relative border-y border-white/10 bg-black/70">
          <Marquee speed={40} gradient={false} pauseOnHover>
            <div className="flex items-center gap-8 py-4">
              {MARQUEE_ITEMS.concat(MARQUEE_ITEMS).map((t, i) => (
                <span key={`marquee-${i}-${t}`} className={`text-xs uppercase tracking-[0.35em] ${
                  t === "•" ? "text-[#D4AF37]" : "text-[#A0A4AB]"
                }`}>
                  {t}
                </span>
              ))}
            </div>
          </Marquee>
        </div>
      </section>

      {/* SERVICES BENTO */}
      {s.show_services && (
      <section className="relative max-w-7xl mx-auto px-6 py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-8 mb-14">
          <div className="md:col-span-6">
            <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">01 · Services</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight">
              A universe of Vedic services, <span className="italic gold-gradient">under one sacred roof.</span>
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 md:pt-4">
            <p className="text-[#A0A4AB] leading-relaxed">
              From personalised Kundali insights to live pooja from Kashi ghats — every ritual, every
              remedy, every guru is now a tap away.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-4">
          {SERVICES.map((s, i) => {
            const Ic = s.icon;
            const wide = i === 0 || i === 4;
            return (
              <Link
                key={s.key}
                to="/services"
                data-testid={`service-card-${s.key}`}
                className={`group relative p-8 rounded-xl bg-[#0A0C10] border border-white/10 hover:border-[#D4AF37]/50 transition-colors duration-300 overflow-hidden ${
                  wide ? "md:col-span-6" : "md:col-span-3"
                }`}
              >
                <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-[#D4AF37]/5 group-hover:bg-[#D4AF37]/10 transition-colors" />
                <Ic size={28} strokeWidth={1.3} className="text-[#D4AF37]" />
                <div className="mt-6">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]/80">{s.tag}</div>
                  <div className="font-serif text-2xl mt-2 text-[#EAE6DF]">{s.name}</div>
                  <p className="text-sm text-[#A0A4AB] mt-3 leading-relaxed">{s.blurb}</p>
                </div>
                <ArrowUpRight size={16} className="absolute right-6 bottom-6 text-[#A0A4AB] group-hover:text-[#D4AF37] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Link>
            );
          })}
        </div>
      </section>
      )}

      {/* AI KUNDALI FEATURE */}
      {s.show_kundali_feature && (
      <section className="relative border-y border-white/10 bg-gradient-to-b from-[#0A0C10] to-[#050507]">
        <div className="max-w-7xl mx-auto px-6 py-24 md:py-32 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">02 · Auto AI Kundali</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight">
              Your birth chart, <span className="italic gold-gradient">read by AI</span> — verified by masters.
            </h2>
            <p className="text-[#A0A4AB] mt-6 leading-relaxed">
              Enter your date, time and place of birth. Our Gemini-powered Vedic engine casts a full
              Kundali interpretation — Lagna, Rashi, Nakshatra, Dasha, career, love & remedies.
            </p>
            <Link
              to="/kundali"
              data-testid="kundali-feature-cta"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition-colors"
            >
              Cast My Kundali <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="md:col-span-7 grid grid-cols-3 gap-3">
            {zodiacs.slice(0, 12).map((z) => (
              <div key={z.key} className="p-4 rounded-lg bg-[#12151C] border border-white/10 hover:border-[#D4AF37]/50 transition-colors">
                <div className="text-3xl text-[#D4AF37]">{z.symbol}</div>
                <div className="font-serif text-lg mt-1">{z.name}</div>
                <div className="font-deva text-xs text-[#A0A4AB]">{z.hi}</div>
                <div className="text-[10px] text-[#6E737D] mt-1">{z.dates}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* LIVE EVENTS */}
      {s.show_live_events && (
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">03 · Live Right Now</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight">
              Live poojas, darshan & <span className="italic gold-gradient">pravachans</span>
            </h2>
          </div>
          <Link to="/live" className="hidden md:flex items-center gap-2 text-sm text-[#D4AF37] hover:text-[#E5C865]">
            View all <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {events.map((e) => (
            <div key={e.id} className="group relative rounded-xl overflow-hidden border border-white/10 bg-[#0A0C10]">
              <div className="relative aspect-[16/10] overflow-hidden">
                <img src={e.cover} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt={e.title} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF5A36] text-white text-[10px] uppercase tracking-widest">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live Soon
                </div>
                {e.is_paid && (
                  <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-[#D4AF37] text-black text-[10px] uppercase tracking-widest font-medium">
                    ₹{e.price}
                  </div>
                )}
              </div>
              <div className="p-5">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]/80 mb-2">
                  {e.type.replace("_", " ")}
                </div>
                <div className="font-serif text-lg leading-snug text-[#EAE6DF]">{e.title}</div>
                <div className="flex items-center gap-4 mt-3 text-xs text-[#A0A4AB]">
                  <span className="flex items-center gap-1"><Users size={12} /> {e.viewers}</span>
                  <span className="flex items-center gap-1"><Clock size={12} /> {new Date(e.start_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* MARKETPLACE preview */}
      {s.show_marketplace_preview && (
      <section className="relative border-t border-white/10 bg-gradient-to-b from-[#050507] to-[#0A0C10]">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="grid md:grid-cols-12 gap-8 mb-12">
            <div className="md:col-span-7">
              <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">04 · Sacred Marketplace</div>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight">
                Rudraksha · Yantra · Gemstones · <span className="italic gold-gradient">energized & authentic.</span>
              </h2>
            </div>
            <div className="md:col-span-4 md:col-start-9 md:pt-4">
              <p className="text-[#A0A4AB] leading-relaxed">
                Every product is prana-pratishtha done, sourced from trusted Vedic artisans across India & Nepal.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            {products.map((p) => (
              <Link key={p.id} to="/shop" data-testid={`product-preview-${p.id}`} className="group">
                <div className="aspect-square rounded-xl overflow-hidden border border-white/10 bg-[#12151C] mb-3">
                  <img src={p.image} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt={p.name} />
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]/80">{p.category}</div>
                <div className="text-sm text-[#EAE6DF] mt-1 leading-snug line-clamp-2">{p.name}</div>
                <div className="text-sm text-[#D4AF37] mt-1 font-medium">₹{p.price.toLocaleString("en-IN")}</div>
              </Link>
            ))}
          </div>

          <div className="mt-10 flex justify-center">
            <Link to="/shop" data-testid="marketplace-cta" className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-white/20 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
              <ShoppingBag size={16} /> Explore Full Marketplace
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* TESTIMONIALS */}
      {s.show_testimonials && (
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">05 · Seekers' Voice</div>
        <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight mb-14 max-w-3xl">
          Words from those who <span className="italic gold-gradient">walked the path</span> with us.
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="p-8 rounded-xl bg-[#0A0C10] border border-white/10 relative">
              <Quote className="text-[#D4AF37]/30 absolute top-6 right-6" size={40} strokeWidth={1} />
              <p className="text-[#EAE6DF] leading-relaxed relative">"{t.text}"</p>
              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="font-serif text-lg">{t.name}</div>
                <div className="text-xs text-[#A0A4AB]">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* FINAL CTA */}
      {s.show_final_cta && (
      <section className="relative overflow-hidden border-t border-white/10">
        <div className="absolute inset-0 starfield opacity-30" />
        <div className="relative max-w-4xl mx-auto px-6 py-28 text-center">
          <div className="font-deva text-5xl text-[#D4AF37] mb-6">ॐ</div>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tighter leading-tight">
            Begin your journey <span className="italic gold-gradient">tonight.</span>
          </h2>
          <p className="text-[#A0A4AB] mt-6 max-w-xl mx-auto">
            Free Kundali. Free daily horoscope. Free chats with Guru-ji AI. All you need is a moment.
          </p>
          <Link
            to="/register"
            data-testid="final-cta-register"
            className="mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition-colors"
          >
            Create My Free Account <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
      )}

      {/* APP DOWNLOAD BANNER */}
      {s.show_app_banner && (s.android_url || s.ios_url) && (
        <section data-testid="app-download-banner" className="relative border-t border-white/10 bg-gradient-to-br from-[#12151C] via-[#0A0C10] to-[#050507]">
          <div className="absolute inset-0 starfield opacity-20 pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Vedacosmos — On The Go</div>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight">
                Take the ancient sky <span className="italic gold-gradient">in your pocket.</span>
              </h2>
              <p className="text-[#A0A4AB] mt-4 max-w-xl">
                Push notifications for daily horoscope, one-tap kundali, live pooja darshan — the full
                super app on Android & iOS.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {s.android_url && (
                  <a href={s.android_url} target="_blank" rel="noopener noreferrer" data-testid="home-android-btn"
                    className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-black border border-white/20 hover:border-[#D4AF37] transition group">
                    <svg viewBox="0 0 24 24" className="w-7 h-7 fill-[#D4AF37]"><path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.807 1.626L15.194 12l2.503-2.492zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z"/></svg>
                    <div className="text-left leading-tight">
                      <div className="text-[10px] text-[#A0A4AB]">GET IT ON</div>
                      <div className="font-serif text-lg text-[#EAE6DF] group-hover:text-[#D4AF37] transition-colors">Google Play</div>
                    </div>
                  </a>
                )}
                {s.ios_url && (
                  <a href={s.ios_url} target="_blank" rel="noopener noreferrer" data-testid="home-ios-btn"
                    className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-black border border-white/20 hover:border-[#D4AF37] transition group">
                    <svg viewBox="0 0 24 24" className="w-7 h-7 fill-[#D4AF37]"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                    <div className="text-left leading-tight">
                      <div className="text-[10px] text-[#A0A4AB]">DOWNLOAD ON THE</div>
                      <div className="font-serif text-lg text-[#EAE6DF] group-hover:text-[#D4AF37] transition-colors">App Store</div>
                    </div>
                  </a>
                )}
              </div>
              <div className="mt-6 text-[10px] uppercase tracking-widest text-[#6E737D]">
                Also installable as a web app — Add to Home Screen from your browser.
              </div>
            </div>

            {/* Phone mockup */}
            <div className="md:col-span-5 flex justify-center">
              <div className="relative w-[220px] aspect-[9/19] rounded-[36px] border-[6px] border-[#D4AF37]/50 bg-black shadow-2xl overflow-hidden">
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-14 h-4 rounded-full bg-black border border-white/10 z-10" />
                <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
                  <div className="font-deva text-4xl text-[#D4AF37] mb-2">ॐ</div>
                  <div className="font-serif text-lg text-[#EAE6DF]">Vedacosmos</div>
                  <div className="text-[9px] uppercase tracking-widest text-[#D4AF37]/70 mt-1">Ancient · Modern</div>
                  <div className="mt-6 p-3 rounded-lg bg-white/5 border border-white/10 w-full">
                    <div className="text-[9px] text-[#A0A4AB]">Today's Horoscope</div>
                    <div className="text-xs text-[#EAE6DF] mt-1">Leo · Warmth & wisdom guide you today ♌</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
